import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  HostListener,
  inject,
  input,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { MatToolbarModule } from '@angular/material/toolbar';
import { TranslatePipe } from '@ngx-translate/core';
import { UserCardComponent } from '../../../features/user/components/user-card/user-card.component';
import { AuthService } from '../../../features/auth/services/auth.service';
import { auth as AuthGen } from '@valvur/shared-grpc';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { useBreakpoint } from '../../composables/useBreakpoint';
import { ParentPatrolPlatformFlagsService } from '../../../features/parent-patrol/services/parent-patrol-platform-flags.service';
import { IdentityNavService } from '../../../features/identity/services/identity-nav.service';
import { FamilyKidsService } from '../../../features/parent-family/services/family-kids.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    RouterLinkActive,
    MatToolbarModule,
    TranslatePipe,
    UserCardComponent,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class HeaderComponent {
  #authService = inject(AuthService);
  #router = inject(Router);
  #breakpoint = useBreakpoint();
  #patrolPlatformFlags = inject(ParentPatrolPlatformFlagsService);
  #identityNav = inject(IdentityNavService);
  #familyKids = inject(FamilyKidsService);

  user = input<AuthGen.User>();
  /** Parent layout: Home / Patrol / Identity links in the toolbar. */
  showNavLinks = input(false);

  showUserMenu = signal(false);

  readonly isMobileHeader = computed(
    () => this.#breakpoint.currentBreakpoint() === 'XSmall',
  );

  readonly showPatrolNavLink = computed(
    () =>
      this.showNavLinks() &&
      this.#patrolPlatformFlags.ready() &&
      this.#patrolPlatformFlags.isAnyEnabled(),
  );

  readonly showIdentityNavLink = computed(
    () => this.showNavLinks() && this.#identityNav.showNavLink(),
  );

  readonly identityNavBadgeCount = computed(
    () => this.#identityNav.attentionCount(),
  );

  readonly showIdentityMenuItem = computed(
    () =>
      this.showNavLinks() &&
      this.isMobileHeader() &&
      this.#identityNav.showNavLink(),
  );

  /**
   * Upgrade CTA is hidden until plans launch.
   * Flip to true (and restore toolbar/menu split) when ready.
   */
  readonly #upgradeCtaEnabled = false;
  readonly showUpgradeInToolbar = computed(
    () => this.#upgradeCtaEnabled && !this.isMobileHeader(),
  );
  readonly showUpgradeInMenu = computed(
    () => this.#upgradeCtaEnabled && this.isMobileHeader(),
  );

  constructor() {
    effect(() => {
      if (!this.showNavLinks()) {
        return;
      }
      // Re-kick when the family shell mounts; services are idempotent.
      void this.#patrolPlatformFlags.whenReady();
      void this.#identityNav.whenReady();
    });
  }

  @HostListener('document:click')
  closeUserMenu(): void {
    this.showUserMenu.set(false);
  }

  isBasicAccount(user: AuthGen.User | undefined): boolean {
    return user?.accountType === 'BASIC';
  }

  kidsCount(): number {
    return this.#familyKids.children().length;
  }

  toggleUserMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.showUserMenu.update((open) => !open);
  }

  logout(): void {
    this.showUserMenu.set(false);
    this.#authService.logout();
  }

  goHome(): void {
    void this.#router.navigate(['/home']);
  }

  toProfile(): void {
    this.showUserMenu.set(false);
    void this.#router.navigate(['/profile']);
  }

  toFamily(): void {
    this.showUserMenu.set(false);
    void this.#router.navigate(['/family']);
  }

  toIdentity(): void {
    this.showUserMenu.set(false);
    void this.#router.navigate(['/identity']);
  }
}
